'use client'

import { Check, Phone } from 'lucide-react'
import { OnboardingLayout } from '@/features/onboarding/components/onboarding-layout'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CASE_STATUS_PIPELINE_BY_FLOW,
  FlowBadge,
  SERVICE_TYPE_TO_FLOW,
  type FlowType,
} from '@/features/cases'
import { useMyInvestorCases, useMyInvestorProfile } from '@/features/investors'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/format/date'
import { cn } from '@/lib/utils'

const SUBTITLES: Record<FlowType, string> = {
  audit: 'Your portfolio audit is moving through review.',
  trades: 'Your trading activity review is moving through review.',
  build: 'Your portfolio plan is moving through review.',
}

/**
 * A pure status tracker — the answer to "where's my thing", nothing else.
 * Documents and call history live on the dashboard once it unlocks. Reads real data (never the
 * onboarding store's client-only `flow`/`status`) — this route only exists inside the
 * authenticated `(investor)` route group, so a real, signed-in investor session is always
 * available here.
 *
 * `caseId` (from the page's `?case=` query param) picks a *specific* case to track — every
 * "Track status" button app-wide now passes its own card's case id. Without it, this falls back
 * to the investor's single most-recently-created case, published or not: previously this excluded
 * published cases entirely, so the moment staff published, the tracker would either jump to a
 * different (older/other-service) case or show the generic "nothing started yet" empty state,
 * even though the investor's own case had just finished. An investor with more than one case in
 * flight relies on `caseId` being passed correctly, not this fallback, to land on the right one.
 */
export function ReviewStatusView({ caseId }: { caseId?: string }) {
  const casesQuery = useMyInvestorCases()
  const { data: investor } = useMyInvestorProfile()

  if (casesQuery.isLoading) {
    return (
      <OnboardingLayout eyebrow="Booked" title="Thanks — you're all set" fitContent>
        <Skeleton className="h-40 w-full" />
      </OnboardingLayout>
    )
  }

  const cases = casesQuery.data ?? []
  const pending =
    (caseId && cases.find((c) => c.id === caseId)) ??
    [...cases].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0]

  if (!pending) {
    return (
      <OnboardingLayout
        eyebrow="Booked"
        title="Thanks — you're all set"
        description="Your advisor hasn't started a service for you yet — they'll be in touch."
        fitContent
      />
    )
  }

  const flow: FlowType = SERVICE_TYPE_TO_FLOW[pending.serviceType]
  const pipeline = CASE_STATUS_PIPELINE_BY_FLOW[flow]
  const currentIndex = pipeline.findIndex((p) => p.key === pending.caseStatus)

  return (
    <OnboardingLayout
      eyebrow="Booked"
      title="Thanks — you're all set"
      description={SUBTITLES[flow]}
      fitContent
    >
      <div className="bg-muted/40 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <FlowBadge flow={flow} />
          <span className="text-muted-foreground text-xs font-medium">{pending.reference}</span>
          {pending.advisorName && (
            <span className="text-muted-foreground text-xs font-medium">
              Advisor: {pending.advisorName}
            </span>
          )}
        </div>
      </div>

      <ol className="relative">
        {pipeline.map((step, i) => {
          const isLast = i === pipeline.length - 1
          // The current status is an *achieved* milestone (e.g. "Consultation Scheduled" means
          // the call has actually been scheduled), so it reads as done/checked immediately — same
          // semantics as client-detail.tsx's StatusTimeline. "Up next" highlights the step after
          // the current one (the one not yet achieved), not the current status itself.
          const done = i <= currentIndex
          const current = i === currentIndex + 1
          return (
            <li key={step.key} className="relative flex items-start gap-3 pb-4 last:pb-0">
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-[11px] top-6 h-[calc(100%-8px)] w-px',
                    done ? 'bg-primary' : 'bg-border',
                  )}
                />
              )}
              <span
                className={cn(
                  'ring-card relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-4',
                  done && 'bg-primary text-primary-foreground',
                  current && 'border-primary bg-card text-primary border-2',
                  !done && !current && 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="size-3" strokeWidth={3} /> : i + 1}
              </span>

              {current ? (
                <div className="bg-primary/5 -mt-1 flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg px-3 py-2">
                  <span className="text-primary min-w-0 truncate text-sm font-semibold">
                    {step.label}
                  </span>
                  <Badge variant="secondary" className="shrink-0">
                    Up next
                  </Badge>
                </div>
              ) : (
                <span
                  className={cn('pt-0.5 text-sm', done ? 'font-medium' : 'text-muted-foreground')}
                >
                  {step.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>

      {/* Same message/condition as status-view.tsx's identical footer, plus the investor's own
          mobile number — safe to show here (unlike the public page) since this route is
          authenticated and already shows the full mobile elsewhere. */}
      <div className="bg-muted mt-4 flex items-center gap-2.5 rounded-lg px-3.5 py-2.5">
        <span className="bg-card text-primary flex size-8 shrink-0 items-center justify-center rounded-full shadow-sm">
          <Phone className="size-4" />
        </span>
        <p className="text-muted-foreground text-xs leading-snug">
          {pending.consultationSlotAt
            ? `Nothing to do for you now, ${pending.advisorName ? `Advisor ${pending.advisorName.split(' ')[0]}` : 'your advisor'} will call you at ${formatDateTime(pending.consultationSlotAt)}${investor?.mobile ? ` on +91 ${investor.mobile}` : ''}.`
            : 'Your advisor will be in touch to schedule a time.'}
        </p>
      </div>
    </OnboardingLayout>
  )
}
